import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import useworkorderPrintPage from "./useworkorderPrintPage";
import PrintIcon from '@mui/icons-material/Print';
import { useTranslation } from 'react-i18next';

export default function PrintWorkOrder() {
  const { reportingData } = useworkorderPrintPage();
  const { t } = useTranslation();

  // Add print styles and animations
  React.useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pulse {
        0% {
          box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
        }
        70% {
          box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
        }
        100% {
          box-shadow: 0 0 0 0 rgba(102, 126, 234, 0);
        }
      }
      
      @media print {
        .print-button {
          display: none !important;
        }
        body {
          margin: 0;
          padding: 0;
        }
        .no-break {
          page-break-inside: avoid;
        }
        h2 {
          page-break-after: avoid;
        }
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Helper function to format dates
  const formatDate = useCallback((dateString: string | null) => {
    if (!dateString) {
      return "Not set";
    }
    return new Date(dateString).toLocaleString();
  }, []);

  // Helper function to format status with color
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case "DONE":
        return "#4CAF50";
      case "IN_PROGRESS":
        return "#2196F3";
      case "PENDING":
        return "#FF9800";
      default:
        return "#757575";
    }
  }, []);

  if (!reportingData) {
    return (
      <div style={{ padding: "20px", fontFamily: "Arial, sans-serif" }}>
        <h2>Loading Work Order Data...</h2>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "20px",
        fontFamily: "Arial, sans-serif",
        backgroundColor: "#f5f5f5",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            padding: "30px",
            textAlign: "center",
          }}
        >
          <h1 style={{ margin: 0, fontSize: "28px", fontWeight: "bold" }}>
            Work Order Report
          </h1>
        </div>

        <div style={{ padding: "30px" }}>
          {/* Basic Information */}
          <section style={{ marginBottom: "30px" }}>
            <h2
              style={{
                color: "#333",
                borderBottom: "3px solid #667eea",
                paddingBottom: "10px",
                marginBottom: "20px",
              }}
            >
              📋 Basic Information
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "15px",
              }}
            >
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                }}
              >
                <strong>Name:</strong> {reportingData.name}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                }}
              >
                <strong>Status:</strong>
                <span
                  style={{
                    color: getStatusColor(reportingData.status),
                    fontWeight: "bold",
                    marginLeft: "8px",
                  }}
                >
                  {reportingData.status}
                </span>
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                }}
              >
                <strong>Assignment Type:</strong> {reportingData.assignmentType}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                }}
              >
                <strong>Completion Reason:</strong>{" "}
                {reportingData.completionReason || "N/A"}
              </div>
            </div>
          </section>

          {/* Timing Information */}
          <section style={{ marginBottom: "30px" }}>
            <h2
              style={{
                color: "#333",
                borderBottom: "3px solid #667eea",
                paddingBottom: "10px",
                marginBottom: "20px",
              }}
            >
              ⏰ Timing Information
            </h2>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
                gap: "15px",
              }}
            >
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#e8f5e8",
                  borderRadius: "6px",
                }}
              >
                <strong>Expected Start:</strong>
                <br />
                {formatDate(reportingData.expectedStartTime)}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#e8f5e8",
                  borderRadius: "6px",
                }}
              >
                <strong>Expected End:</strong>
                <br />
                {formatDate(reportingData.expectedEndTime)}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#fff3e0",
                  borderRadius: "6px",
                }}
              >
                <strong>Actual Start:</strong>
                <br />
                {formatDate(reportingData.actualStartTime)}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#fff3e0",
                  borderRadius: "6px",
                }}
              >
                <strong>Actual End:</strong>
                <br />
                {formatDate(reportingData.actualEndTime)}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f3e5f5",
                  borderRadius: "6px",
                }}
              >
                <strong>Created:</strong>
                <br />
                {formatDate(reportingData.createdAt)}
              </div>
              <div
                style={{
                  padding: "15px",
                  backgroundColor: "#f3e5f5",
                  borderRadius: "6px",
                }}
              >
                <strong>Updated:</strong>
                <br />
                {formatDate(reportingData.updatedAt)}
              </div>
            </div>
          </section>

          {/* Assigned User */}
          {reportingData.assignedUser && (
            <section style={{ marginBottom: "30px" }}>
              <h2
                style={{
                  color: "#333",
                  borderBottom: "3px solid #667eea",
                  paddingBottom: "10px",
                  marginBottom: "20px",
                }}
              >
                👤 Assigned User
              </h2>
              <div
                style={{
                  padding: "20px",
                  backgroundColor: "#e3f2fd",
                  borderRadius: "6px",
                  border: "1px solid #bbdefb",
                }}
              >
                <div>
                  <strong>Name:</strong> {reportingData.assignedUser.name}
                </div>
                {reportingData.assignedUser.email && (
                  <div>
                    <strong>Email:</strong> {reportingData.assignedUser.email}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Work Queues */}
          {reportingData.workQueues && reportingData.workQueues.length > 0 && (
            <section style={{ marginBottom: "30px" }}>
              <h2
                style={{
                  color: "#333",
                  borderBottom: "3px solid #667eea",
                  paddingBottom: "10px",
                  marginBottom: "20px",
                }}
              >
                🔧 Work Queues ({reportingData.workQueues.length})
              </h2>
              {reportingData.workQueues.map((queue, index) => (
                <div
                  key={queue.id}
                  style={{
                    marginBottom: "20px",
                    padding: "20px",
                    backgroundColor: "#fafafa",
                    borderRadius: "8px",
                    border: "1px solid #e0e0e0",
                  }}
                >
                  <h3 style={{ color: "#555", marginTop: 0 }}>
                    Queue #{index + 1}
                  </h3>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "10px",
                    }}
                  >
                    <div>
                      <strong>Type:</strong> {queue.type}
                    </div>
                    <div>
                      <strong>Started:</strong> {queue.isStarted ? "Yes" : "No"}
                    </div>
                    <div>
                      <strong>Canceled:</strong>{" "}
                      {queue.isCanceled ? "Yes" : "No"}
                    </div>
                  </div>

                  {/* Asset Information */}
                  {queue.asset && (
                    <div
                      style={{
                        marginTop: "15px",
                        padding: "10px",
                        backgroundColor: "#f0f0f0",
                        borderRadius: "4px",
                      }}
                    >
                      <strong>Asset:</strong> {queue.asset.name}
                    </div>
                  )}

                  {/* Plan Task Information */}
                  {queue.planTask && (
                    <div
                      style={{
                        marginTop: "15px",
                        padding: "10px",
                        backgroundColor: "#e8f4fd",
                        borderRadius: "4px",
                      }}
                    >
                      <div>
                        <strong>Task:</strong> {queue.planTask.name}
                      </div>
                      <div>
                        <strong>Steps:</strong> {queue.planTask.stepsCount}
                      </div>
                      <div>
                        <strong>Priority:</strong> {queue.planTask.priority}
                      </div>
                      <div>
                        <strong>Time:</strong> {queue.planTask.taskTime}
                      </div>
                      {queue.planTask.serviceType && (
                        <div>
                          <strong>Service Type:</strong>{" "}
                          {queue.planTask.serviceType.name}
                          {queue.planTask.serviceType.skills.length > 0 && (
                            <div style={{ marginTop: "5px" }}>
                              <strong>Skills:</strong>{" "}
                              {queue.planTask.serviceType.skills.join(", ")}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </section>
          )}

          {/* Spare Parts Consumption */}
          {reportingData.sparePartsConsumption &&
            reportingData.sparePartsConsumption.length > 0 && (
              <section style={{ marginBottom: "30px" }}>
                <h2
                  style={{
                    color: "#333",
                    borderBottom: "3px solid #667eea",
                    paddingBottom: "10px",
                    marginBottom: "20px",
                  }}
                >
                  🔩 Spare Parts Consumption
                </h2>
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      backgroundColor: "white",
                      borderRadius: "6px",
                      overflow: "hidden",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <thead>
                      <tr
                        style={{ backgroundColor: "#667eea", color: "white" }}
                      >
                        <th style={{ padding: "15px", textAlign: "left" }}>
                          Part Name
                        </th>
                        <th style={{ padding: "15px", textAlign: "center" }}>
                          Consumed
                        </th>
                        <th style={{ padding: "15px", textAlign: "center" }}>
                          Returned
                        </th>
                        <th style={{ padding: "15px", textAlign: "center" }}>
                          Net Consumed
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportingData.sparePartsConsumption.map(
                        (consumption, index) => (
                          <tr
                            key={consumption.sparePart.id}
                            style={{
                              backgroundColor:
                                index % 2 === 0 ? "#f8f9fa" : "white",
                              borderBottom: "1px solid #e0e0e0",
                            }}
                          >
                            <td style={{ padding: "15px" }}>
                              <div>
                                <strong>{consumption.sparePart.name}</strong>
                              </div>
                              {consumption.sparePart.description && (
                                <div
                                  style={{ fontSize: "12px", color: "#666" }}
                                >
                                  {consumption.sparePart.description}
                                </div>
                              )}
                            </td>
                            <td
                              style={{ padding: "15px", textAlign: "center" }}
                            >
                              {consumption.totalQuantityConsumed}
                            </td>
                            <td
                              style={{ padding: "15px", textAlign: "center" }}
                            >
                              {consumption.totalQuantityReturned}
                            </td>
                            <td
                              style={{
                                padding: "15px",
                                textAlign: "center",
                                fontWeight: "bold",
                              }}
                            >
                              {consumption.netQuantityConsumed}
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

          {/* Timeline */}
          {reportingData.timeline && reportingData.timeline.length > 0 && (
            <section style={{ marginBottom: "30px" }}>
              <h2
                style={{
                  color: "#333",
                  borderBottom: "3px solid #667eea",
                  paddingBottom: "10px",
                  marginBottom: "20px",
                }}
              >
                📅 Timeline ({reportingData.timeline.length} events)
              </h2>
              <div>
                {reportingData.timeline.map((entry, index) => (
                  <div
                    key={index}
                    style={{
                      marginBottom: "15px",
                      padding: "15px",
                      backgroundColor: "white",
                      borderLeft: "4px solid #667eea",
                      borderRadius: "0 6px 6px 0",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "10px",
                      }}
                    >
                      <span style={{ fontWeight: "bold", color: "#667eea" }}>
                        {t(`spareParts.${entry.action}`)}
                      </span>
                      <span style={{ fontSize: "12px", color: "#666" }}>
                        {formatDate(entry.time)}
                      </span>
                    </div>
                    <div style={{ fontSize: "14px", color: "#555" }}>
                      <div>
                        <strong>Type:</strong> {entry.type}
                      </div>
                      <div>
                        <strong>User:</strong> {entry.user.name}
                        {entry.user.email && ` (${entry.user.email})`}
                      </div>
                      {entry.workQueueName && (
                        <div>
                          <strong>Work Queue:</strong> {entry.workQueueName}
                        </div>
                      )}
                      {entry.assetName && (
                        <div>
                          <strong>Asset:</strong> {entry.assetName}
                        </div>
                      )}
                      {entry.stepName && (
                        <div>
                          <strong>Step:</strong> {entry.stepName}
                        </div>
                      )}
                      {entry.value && (
                        <div>
                          <strong>Value:</strong> {entry.value}
                        </div>
                      )}
                      {entry.sparePart && (
                        <div>
                          <strong>Spare Part:</strong> {entry.sparePart.name}
                          {entry.sparePart.quantityChange && (
                            <span>
                              {" "}
                              (Quantity: {entry.sparePart.quantityChange})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Fixed Print Button */}
      <button
        className="print-button"
        onClick={() => window.print()}
        style={{
          position: "fixed",
          bottom: "30px",
          right: "30px",
          width: "70px",
          height: "70px",
          borderRadius: "50%",
          backgroundColor: "#667eea",
          border: "none",
          boxShadow:
            "0 8px 20px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)",
          cursor: "pointer",
          fontSize: "24px",
          color: "white",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          animation: "pulse 2s infinite",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#5a67d8";
          e.currentTarget.style.transform = "scale(1.1) translateY(-2px)";
          e.currentTarget.style.boxShadow =
            "0 12px 30px rgba(102, 126, 234, 0.5), 0 6px 18px rgba(0, 0, 0, 0.2)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#667eea";
          e.currentTarget.style.transform = "scale(1) translateY(0)";
          e.currentTarget.style.boxShadow =
            "0 8px 20px rgba(102, 126, 234, 0.4), 0 4px 12px rgba(0, 0, 0, 0.15)";
        }}
        title="Print Work Order Report"
      >
        <PrintIcon fontSize="large" />
      </button>
    </div>
  );
}
